/**
 * Cookie utility functions
 * Centralized cookie management for authentication
 */

import Cookies from 'js-cookie';

// Cookie configuration
const COOKIE_OPTIONS = {
  expires: 7, // 7 days
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'strict' as const,
};

const REFRESH_TOKEN_OPTIONS = {
  expires: 30, // 30 days
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'strict' as const,
};

// Cookie keys
export const COOKIE_KEYS = {
  IS_AUTHENTICATED: 'isAuthenticated',
  USER_NAME: 'userName',
  TOKEN: 'token',
  REFRESH_TOKEN: 'refreshToken',
  USER_DATA: 'userData',
} as const;

/**
 * Set authentication cookies
 */
export const setAuthCookies = (data: {
  token?: string;
  refreshToken?: string;
  userName: string;
  userData?: any;
}) => {
  // Set authentication status
  Cookies.set(COOKIE_KEYS.IS_AUTHENTICATED, 'true', COOKIE_OPTIONS);
  Cookies.set(COOKIE_KEYS.USER_NAME, data.userName, COOKIE_OPTIONS);

  // Set tokens if provided
  if (data.token) {
    Cookies.set(COOKIE_KEYS.TOKEN, data.token, COOKIE_OPTIONS);
  }
  if (data.refreshToken) {
    Cookies.set(
      COOKIE_KEYS.REFRESH_TOKEN,
      data.refreshToken,
      REFRESH_TOKEN_OPTIONS
    );
  }

  // Set user data if provided
  if (data.userData) {
    Cookies.set(
      COOKIE_KEYS.USER_DATA,
      JSON.stringify(data.userData),
      COOKIE_OPTIONS
    );
  }
};

/**
 * Clear all authentication cookies
 */
export const clearAuthCookies = () => {
  Object.values(COOKIE_KEYS).forEach(key => {
    Cookies.remove(key);
  });
};

/**
 * Get authentication status from cookies
 */
export const getAuthStatus = () => {
  const isAuthenticated = Cookies.get(COOKIE_KEYS.IS_AUTHENTICATED) === 'true';
  const userName = Cookies.get(COOKIE_KEYS.USER_NAME);
  const token = Cookies.get(COOKIE_KEYS.TOKEN);
  const userData = Cookies.get(COOKIE_KEYS.USER_DATA);

  return {
    isAuthenticated,
    userName: userName || null,
    token: token || null,
    userData: userData ? JSON.parse(userData) : null,
  };
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return Cookies.get(COOKIE_KEYS.IS_AUTHENTICATED) === 'true';
};

/**
 * Get authentication token
 */
export const getToken = (): string | null => {
  return Cookies.get(COOKIE_KEYS.TOKEN) || null;
};

/**
 * Get refresh token
 */
export const getRefreshToken = (): string | null => {
  return Cookies.get(COOKIE_KEYS.REFRESH_TOKEN) || null;
};

/**
 * Get user email
 */
export const getUserEmail = (): string | null => {
  return Cookies.get(COOKIE_KEYS.USER_NAME) || null;
};

/**
 * Get user data
 */
export const getUserData = (): any | null => {
  const userData = Cookies.get(COOKIE_KEYS.USER_DATA);
  return userData ? JSON.parse(userData) : null;
};

export default {
  setAuthCookies,
  clearAuthCookies,
  getAuthStatus,
  isAuthenticated,
  getToken,
  getRefreshToken,
  getUserEmail,
  getUserData,
};
